
import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge";
import { documentGroupApi } from "@/services/api";
import { X } from "lucide-react";

// Pill colors for document groups
const PILL_COLORS = [
  "bg-blue-100 text-blue-800 border-blue-300",
  "bg-green-100 text-green-800 border-green-300",
  "bg-purple-100 text-purple-800 border-purple-300",
  "bg-yellow-100 text-yellow-800 border-yellow-300",
  "bg-pink-100 text-pink-800 border-pink-300",
  "bg-indigo-100 text-indigo-800 border-indigo-300",
  "bg-red-100 text-red-800 border-red-300",
  "bg-orange-100 text-orange-800 border-orange-300",
  "bg-teal-100 text-teal-800 border-teal-300",
];

interface DocumentGroupListProps {
  selectedGroups: number[];
  onRemoveGroup: (groupId: number) => void;
  managementCompanyId?: number;
  associationId?: number;
}

export const DocumentGroupList = ({
  selectedGroups,
  onRemoveGroup,
  managementCompanyId,
  associationId,
}: DocumentGroupListProps) => {
  const { data: documentGroups = [], isLoading } = useQuery({
    queryKey: ["document-groups", { management_company_id: managementCompanyId }],
    queryFn: () => documentGroupApi.list({
      queryKey: ["document-groups", { management_company_id: managementCompanyId }]
    }),
    enabled: selectedGroups.length > 0
  });

  if (isLoading) return <div>Loading...</div>;
  
  if (selectedGroups.length === 0) {
    return <div className="text-sm text-muted-foreground">No document groups selected</div>;
  }

  return (
    <div className="flex flex-wrap gap-2">
      {selectedGroups.map((groupId, index) => {
        const group = documentGroups.find((g) => g.id === groupId);
        if (!group) return null;
        
        // Check if this is the association's linked document group
        const isLinkedGroup = group.association_id === associationId;
        
        // Get a color based on the index, cycling through the colors array
        const colorClass = PILL_COLORS[index % PILL_COLORS.length];
        
        return (
          <Badge 
            key={group.id} 
            className={`${colorClass} hover:font-bold transition-none border px-3 py-1 ${isLinkedGroup ? 'cursor-default' : 'cursor-pointer'} flex items-center gap-1`}
            onClick={() => !isLinkedGroup && onRemoveGroup(group.id)}
            title={isLinkedGroup ? "This document group is linked to this association and cannot be removed" : ""}
          >
            {group.name}
            {!isLinkedGroup && <X size={14} className="ml-1" />}
          </Badge>
        );
      })}
    </div>
  );
};
